/* eslint-disable object-shorthand */
import { isEmail, isExternalUser } from '@atlaskit/user-picker';

import type {
	FlagDescriptor,
	ShowFlagArgs,
} from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { AccessRequestsTableProps } from '@confluence/access-requests/entry-points/AccessRequestsTable.tsx';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import type {
	UserPickerPrincipal,
	Role,
	StagedPrincipalsAddMode,
	UserPrincipal,
	GeneralAccessLevel,
} from '../controlled/commonTypes.tsx';
// eslint-disable-next-line import/no-cycle
import { validateRestrictedPrincipals } from '../controlled/resolveRestrictions/validateRestrictedPrincipals.ts';

import { preloadSpaceRoleMode } from './preloadSpaceRoleMode.ts';
import type { RefetchQueries } from './queries/useQueries.ts';
import { type ControllerExtras, type ControllerProps, type QueriesState } from './Controller.tsx';
import { type MasterStateSetter, type MasterState } from './masterState.ts';
import { makeRefreshDialogState } from './refreshDialogStateUtil.ts';
import type { DerivedState } from './derivedState.ts';
import type { CtaFlowEventDispatcher } from './ctaFlowController.ts';
import {
	stagePrincipalReassignment,
	stagePrincipalRemoval,
	convertUserPickerPrincipal,
	stagePrincipalAddition,
	filterStagedAdditionsByPrincipals,
} from './principals.ts';
import { makePrincipalsMap } from './principalsMap.ts';

export type Actions = {
	/** Refresh dialog state after mutations */
	refreshDialogState: () => Promise<void>;
	/**
	 * User tried to dismiss the main dialog but has not had a chance to
	 * reconsider if they have changes staged
	 */
	dismiss: () => void;

	/** User reconsidered closing without saving */
	dismissCloseConfirmationDialog: () => void;

	/**
	 * A dialog that was potentially the last in the workflow just completed its
	 * exit animation. If the teardown controller is primed to end the session
	 * after such animations complete, it should do so now.
	 */
	potentialFinalDialogDidCompleteClose: () => void;

	/** User confirmed closing without saving */
	closeWithoutSaving: () => void;

	/**
	 * User staged a change to the open/restricted aspect of the General Access
	 * control
	 */
	changeGeneralAccessOpenRestricted: (becomeRestricted: boolean) => void;

	/**
	 * User staged a change to the can-view/can-edit aspect of the open access
	 * state for the General Access control
	 */
	changeGeneralAccessCanViewCanEdit: (becomeCanEdit: boolean) => void;

	/** Change the staged principals list */
	changeStagedPrincipals: (principalsStagerPrincipals: UserPickerPrincipal[]) => void;

	/**
	 * Publish modal (staged mode): commit currently selected principals from the stager
	 * into the Specific Access staged additions, then clear the stager list.
	 */
	commitStagerPrincipalsToSpecificAccess: () => void;

	/** Change the staged setting for what to do with staged principals */
	changeAddMode: (
		addMode: StagedPrincipalsAddMode,
		principalsStagerPrincipals: UserPickerPrincipal[],
	) => void;

	/** Change the role a principal in the specific access list should have */
	changeSpecificAccessRole: (role: Role, id: string) => void;

	/** Remove a principal from the Specific Access list */
	removeSpecificAccessPrincipal: (id: string) => void;

	/** User wants all specific access principals removed */
	removeAllSpecificAccessPrincipals: () => void;

	/** User tried to dismiss the Space Access Details dialog */
	dismissSpaceAccessDetails: () => void;

	/** User opens Space Access Details dialog */
	openSpaceAccessDetails: () => void;

	/** User opens Open Access Warning dialog */
	showOpenAccessWarningDialog: () => void;

	/** User closes Open Access Warning dialog */
	closeOpenAccessWarningDialog: () => void;

	/** User cancels restrict access confirmation dialog */
	cancelRestrictAccessConfirmation: () => void;

	/** User confirms restrict access confirmation dialog */
	confirmRestrictAccessConfirmation: () => void;

	/** User tried to dismiss the Inspect Perms dialog */
	openInspectPermsDialog: () => void;

	/** User tried to dismiss the Inspect Perms dialog */
	dismissInspectPermsDialog: () => void;

	/**
	 * User clicks the Public link button in the Distribution Footer
	 */
	openPublicLinkDialog: () => void;

	/**
	 * User clicks the back button in the Public Link dialog
	 */
	closePublicLinkDialog: () => void;

	/**
	 * User clicks the Slack button in the Distribution Footer
	 */
	openShareToSlackDialog: () => void;

	/**
	 * User clcks on the Access Requests button in the Header
	 */
	openAccessRequestsDialog: () => void;

	/**
	 * User clcks on the Access Requests button in the Header
	 */
	closeAccessRequestsDialog: () => void;

	/**
	 * User clicks the back button in the Share to Slack dialog
	 */
	closeShareToSlackDialog: () => void;

	/**
	 * Clear the focus element if it is the last triggered dialog variant
	 */
	clearElementToFocus: () => void;

	/**
	 * Generic action to show any flag
	 */
	showFlag: (flag: Omit<FlagDescriptor, 'id'>) => Promise<FlagDescriptor>;

	/**
	 * Generic action to show error flag
	 */
	showErrorFlag: (flag: ShowFlagArgs) => Promise<FlagDescriptor>;

	/** User clicked the CTA, whatever that might have said at the time */
	handleCtaClick: () => void;

	/** User staged a change to the optional message */
	changeOptionalMessage: (message: string) => void;

	/** Set the state of the should-notify checkbox */
	setNotifyChecked: (newValue: boolean) => void;

	/**
	 * Toggle whether to show the message composer under the notify-new-people
	 * checkbox
	 */
	toggleNotifyAddMessage: () => void;

	/** User confirms that they want to include groups in notifications */
	doNotifyGroupsConfirmed: () => void;

	/** User confirms that they do not want to include groups in notifications */
	doNotNotifyGroupsConfirmed: () => void;

	/** User confirms that they want to notify many users */
	doNotifyManyUsersConfirmed: () => void;

	/** User cancels notifying many users */
	cancelNotifyManyUsers: () => void;

	/** User confirms that they want to ignore access restrictions and continue */
	ignoreRestrictionsAndContinue: (isGuestError?: boolean) => void;

	/** Restrictions are fixed or skipped  */
	resolveRestrictionsComplete: () => void;

	/** Toggle whether to show the Specific Access list */
	toggleSpecificAccessExpanded: () => void;

	/** Open the specific access dialog */
	openSpecificAccessDialog: () => void;

	/** Close the specific access dialog*/
	closeSpecificAccessDialog: () => void;

	/** Used by Access Requests Dialog to try and resolve blocked access for a user (one at a time) */
	maybeStartBlockedAccessFlowForUser: AccessRequestsTableProps['maybeStartBlockedAccessFlowForUser'];

	/** Closes the Resolve Restrictions overlay dialog for Access Requests */
	closeAccessRequestsResolveRestrictionsDialog: () => void;

	/** Closes the Warn Restrictions overlay dialog for Access Requests */
	closeAccessRequestsWarnRestrictionsDialog: () => void;

	/** Set whether there are unsaved changes in the Access Requests dialog */
	setAccessRequestsChanges: (hasChanges: boolean) => void;

	/** Set status of skipped principals on User Picker option */
	markPrincipalsAsSkipped: (skippedPrincipals: string[]) => void;

	/** Set status of invited principals on User Picker option */
	markPrincipalsAsInvited: (
		invitedPrincipals: { id: string; email: string }[],
		errorPrincipals: { email: string }[],
	) => void;
};

const shouldPrincipalHavePendingAction = (principal: UserPickerPrincipal) => {
	return (
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		isEmail(principal) &&
		principal.isPendingAction === undefined &&
		fg('twcg-444-invite-usd-improvements-m2-gate') &&
		expValEquals('twcg-444-invite-usd-improvements-m2', 'cohort', 'variation')
	);
};

const withPendingActionPrincipals = (principals: UserPickerPrincipal[]): UserPickerPrincipal[] => {
	return principals.map((principal) => {
		if (shouldPrincipalHavePendingAction(principal)) {
			return { ...principal, isPendingAction: true };
		}
		return principal;
	});
};

const stageEligiblePrincipals = ({
	principalsStagerPrincipals,
	queriesState,
	addMode,
	setMasterState,
}: {
	principalsStagerPrincipals: UserPickerPrincipal[];
	queriesState: QueriesState;
	addMode?: StagedPrincipalsAddMode;
	setMasterState: MasterStateSetter;
}) => {
	const currentUser = queriesState.currentUser;
	const currentUserIsNotInBaseQuery = !queriesState.contentRestrictions.principals.some(
		({ principal }) => principal.id === currentUser.id,
	);

	for (const platformPrincipal of [...principalsStagerPrincipals].reverse()) {
		if (platformPrincipal.type === 'email' || platformPrincipal.type === 'external_user') {
			continue;
		}

		const principal = convertUserPickerPrincipal(platformPrincipal);
		const isCurrentUser = principal.id === currentUser.id;
		if (!isCurrentUser) {
			stagePrincipalAddition(setMasterState, principal, addMode);
		} else {
			// If current user is not gaining initial access, don't allow them to demote themselves
			currentUserIsNotInBaseQuery && stagePrincipalAddition(setMasterState, principal, addMode);
		}
	}
};

/**
 * Stages all eligible principals currently selected in the stager into the
 * Specific Access staged additions list.
 */
function addStagerPrincipalsToSpecificAccess({
	principalsStagerPrincipals,
	queriesState,
	addMode,
	setMasterState,
}: {
	principalsStagerPrincipals: UserPickerPrincipal[];
	queriesState: QueriesState;
	addMode: StagedPrincipalsAddMode;
	setMasterState: MasterStateSetter;
}) {
	stageEligiblePrincipals({ principalsStagerPrincipals, queriesState, addMode, setMasterState });

	// Clear the stager list after committing.
	setMasterState(
		(s: MasterState): MasterState => ({
			...s,
			principalsStagerPrincipals: [],
		}),
	);
}

/**
 * Adds principals to the principal map and stages them in the staged addition list.
 * The staged addition list holds principal that will be processed when the CTA is clicked.
 *
 * This callback is currently used for:
 * - Updating the principal stager list
 * - Handling dropdown changes for permissions
 */
function addPrincipals({
	principalsStagerPrincipals,
	addMode,
	queriesState,
	setMasterState,
}: {
	principalsStagerPrincipals: UserPickerPrincipal[];
	addMode?: StagedPrincipalsAddMode;
	queriesState: QueriesState;
	setMasterState: MasterStateSetter;
}) {
	// Add the staged principals, one at a time, such that the first will end up on top
	stageEligiblePrincipals({ principalsStagerPrincipals, queriesState, addMode, setMasterState });

	// Remove any staged additions not present in the updated principals list
	filterStagedAdditionsByPrincipals(setMasterState, principalsStagerPrincipals);

	// Update the staged principals list
	const principalsWithPendingAction = withPendingActionPrincipals(principalsStagerPrincipals);
	setMasterState(
		(s: MasterState): MasterState => ({
			...s,
			principalsStagerPrincipals: principalsWithPendingAction,
		}),
	);
}

/**
 * Builds a CtaSaveFlowEvent with common configuration.
 * Extracted to eliminate duplication across mutation dispatch functions.
 */
function buildCtaSaveFlowEvent({
	masterState,
	derivedState,
	queriesState,
	controllerProps,
	controllerExtras,
	dispatchCtaFlowEvent: dispatchFn,
	generalAccess,
	isGeneralAccessMutation = false,
	isRestrictionsAccessMutation = false,
}: {
	masterState: MasterState;
	derivedState: DerivedState;
	queriesState: QueriesState;
	controllerProps: ControllerProps;
	controllerExtras: ControllerExtras;
	dispatchCtaFlowEvent: CtaFlowEventDispatcher;
	generalAccess?: GeneralAccessLevel;
	isGeneralAccessMutation?: boolean;
	isRestrictionsAccessMutation?: boolean;
}): void {
	const { contentId, contentType, contentSubType, isUnpublishedDraft } = controllerProps;
	const {
		addMode,
		optionalMessage,
		shouldResolveMismatchedRestrictions,
		principalsStagerPrincipals,
	} = masterState;
	const {
		edition,
		numberOfAncestors,
		userCanEditRestrictions,
		currentUser,
		shouldCurrentUserBeAddedToList,
	} = queriesState;
	const { includeInvites, generalAccess: derivedGeneralAccess } = derivedState;
	const { mutateGeneralAccess, mutateRestrictionsAccess } = controllerExtras;
	const finalGeneralAccess = generalAccess ?? derivedGeneralAccess;

	const {
		mutateContentRestrictions,
		sendNotification,
		successFlag,
		errorFlag,
		fireAnalyticEvent,
		experienceTracker,
		callResolveMismatchedRestrictions,
	} = controllerExtras;

	const updatedPrincipalMap = makePrincipalsMap(masterState, queriesState, {
		shouldIncludeUserPickerStagedAdditions: true,
		generalAccess: finalGeneralAccess,
	});

	dispatchFn({
		contentId,
		edition,
		numberOfAncestors,
		type: 'cta',
		addMode,
		mutateContentRestrictions,
		currentUser,
		sendNotification,
		successFlag,
		errorFlag,
		beginTeardown: () => {},
		isMutatingAction: true,
		isNotifyingAction: false,
		userCanEditRestrictions,
		generalAccess: finalGeneralAccess,
		principalsMap: updatedPrincipalMap,
		optionalMessage,
		principalsStagerPrincipals,
		stagedNotificationRecipients: [],
		validateRestrictedPrincipals,
		experienceTracker,
		fireAnalyticEvent,
		contentSubType,
		contentType,
		callResolveMismatchedRestrictions,
		isResolvingAction: shouldResolveMismatchedRestrictions,
		isUnpublishedDraft,
		includeInvites,
		mutateGeneralAccess,
		mutateRestrictionsAccess,
		isGeneralAccessMutation,
		isRestrictionsAccessMutation,
		shouldCurrentUserBeAddedToList,
	});
}

export function getActions(
	controllerProps: ControllerProps,
	controllerExtras: ControllerExtras,
	masterState: MasterState,
	derivedState: DerivedState,
	queriesState: QueriesState,
	setMasterState: MasterStateSetter,
	dispatchCtaFlowEvent: CtaFlowEventDispatcher,
	refetchQueries: RefetchQueries,
): Actions {
	// Dispatch wrappers for live mutations
	const refreshDialogState = makeRefreshDialogState(setMasterState, refetchQueries, {
		principalsStagerPrincipals: [],
		// Clear all deltas - this lets the fresh queriesState data show through
		generalAccessDelta: 'none',
		specificAccessDelta: {
			removeAll: false,
			userPickerStagedAdditions: [],
			removals: new Set(),
			reassignments: new Map(),
		},
		...(expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') && {
			dialogMode: 'main',
			notifyCheckedIfAvailable: true,
			addMode: 'as-editor',
			optionalMessage: '',
			showMessageComposerUnderCheckbox: false,
		}),
	});

	const dispatchGeneralAccessMutation = (liveGeneralAccessLevel: GeneralAccessLevel) => {
		buildCtaSaveFlowEvent({
			masterState,
			derivedState,
			queriesState,
			controllerProps,
			controllerExtras,
			dispatchCtaFlowEvent,
			generalAccess: liveGeneralAccessLevel,
			isGeneralAccessMutation: true,
		});
	};

	return {
		refreshDialogState,
		dismiss() {
			// TODO: if we're in the middle of saving, we should probably ignore this
			// request. Check with designers.

			const { isChanged } = derivedState;
			const { principalsStagerPrincipals } = masterState;

			if (isChanged || principalsStagerPrincipals.length > 0) {
				setMasterState(
					(s: MasterState): MasterState => ({
						...s,
						isCloseConfirmationOpen: true,
					}),
				);
			} else {
				controllerExtras.teardownController.tearDownAfterPotentialFinalDialogCompletesClose();
				setMasterState(
					(s: MasterState): MasterState => ({
						...s,
						sessionState: 'exiting',
					}),
				);
			}
		},
		dismissCloseConfirmationDialog() {
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					isCloseConfirmationOpen: false,
				}),
			);
		},
		potentialFinalDialogDidCompleteClose() {
			controllerExtras.teardownController.potentialFinalDialogDidCompleteClose();
		},
		closeWithoutSaving() {
			controllerExtras.teardownController.tearDownAfterPotentialFinalDialogCompletesClose();
			setMasterState((s: MasterState): MasterState => {
				const baseState = {
					...s,
					isCloseConfirmationOpen: false,
					sessionState: 'exiting' as const,
					hasAccessRequestsChanges: false,
				};

				if (expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
					return {
						...baseState,
						dialogMode: 'main',
						notifyCheckedIfAvailable: true,
						addMode: 'as-editor',
						optionalMessage: '',
						showMessageComposerUnderCheckbox: false,
					};
				}

				return baseState;
			});
		},
		changeGeneralAccessOpenRestricted(becomeRestricted: boolean) {
			const { isUnpublishedDraft } = controllerProps;
			const { generalAccess, shouldShowDeletedGroupDetails, persistenceMode } = derivedState;
			const { principalsStagerPrincipals } = masterState;

			const openLevelToSet =
				isUnpublishedDraft || shouldShowDeletedGroupDetails ? 'open-can-edit' : 'open-can-view';

			/** This is when the user changes general access to open to "clear" deleted group's restrictions on page.
			 * We need to communicate to the singleContent query that hasRestrictions now needs to be false, we set a value in the master state
			 * called shouldResolveMismatchedRestrictions to true which decides that the unlockIcon now needs to be shown, and fires off updateRestrictions in the
			 * flow controller later on.
			 */
			if (shouldShowDeletedGroupDetails) {
				//check that user picker list is empty, otherwise proceed as usual
				if (principalsStagerPrincipals.length === 0) {
					setMasterState(
						(s: MasterState): MasterState => ({
							...s,
							generalAccessDelta: 'open-can-edit',
							shouldResolveMismatchedRestrictions: true,
						}),
					);
					if (expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
						dispatchGeneralAccessMutation(becomeRestricted ? 'restricted' : openLevelToSet);
					}

					return;
				}
			}

			// If the general access level did not change, don't do anything
			if (
				(becomeRestricted && generalAccess === 'restricted') ||
				(!shouldShowDeletedGroupDetails &&
					!becomeRestricted &&
					(generalAccess === 'open-can-view' || generalAccess === 'open-can-edit'))
			) {
				return;
			}

			if (
				becomeRestricted &&
				persistenceMode === 'live' &&
				(generalAccess === 'open-can-view' || generalAccess === 'open-can-edit') &&
				expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
			) {
				setMasterState(
					(s: MasterState): MasterState => ({
						...s,
						generalAccessDelta: 'restricted',
						isRestrictAccessConfirmationDialogOpen: true,
						previousGeneralAccess: generalAccess,
						pendingGeneralAccess: 'restricted',
					}),
				);
				return;
			}

			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					generalAccessDelta: becomeRestricted ? 'restricted' : openLevelToSet,
				}),
			);

			if (expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
				if (persistenceMode !== 'staged') {
					dispatchGeneralAccessMutation(becomeRestricted ? 'restricted' : openLevelToSet);
				}
			}
		},
		changeGeneralAccessCanViewCanEdit(becomeCanEdit: boolean) {
			const { generalAccess } = derivedState;
			// If the general access level did not change, don't do anything
			if (
				(becomeCanEdit && generalAccess === 'open-can-edit') ||
				(!becomeCanEdit && generalAccess === 'open-can-view')
			) {
				return;
			}
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					generalAccessDelta: becomeCanEdit ? 'open-can-edit' : 'open-can-view',
				}),
			);

			if (expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
				if (derivedState.persistenceMode !== 'staged') {
					dispatchGeneralAccessMutation(becomeCanEdit ? 'open-can-edit' : 'open-can-view');
				}
			}
		},
		changeStagedPrincipals(principalsStagerPrincipals: UserPickerPrincipal[]) {
			const { persistenceMode } = derivedState;
			// In persistence experiment staged mode, the stager list is a temporary selection.
			// Principals are only committed into Specific Access when the user clicks the Add button.
			if (
				persistenceMode === 'staged' &&
				expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
			) {
				const { dialogMode: currentDialogMode } = masterState;

				// Determine the new dialog mode based on whether principals are staged
				const newDialogMode = principalsStagerPrincipals.length > 0 ? 'share' : 'main';

				// Check if we're transitioning from share mode back to main mode (all principals removed)
				const isExitingShareMode = currentDialogMode === 'share' && newDialogMode === 'main';

				setMasterState((s: MasterState): MasterState => {
					const updatedState: MasterState = {
						...s,
						principalsStagerPrincipals: withPendingActionPrincipals(principalsStagerPrincipals),
						dialogMode: newDialogMode,
					};

					if (isExitingShareMode) {
						updatedState.notifyCheckedIfAvailable = true;
						updatedState.addMode = 'as-editor';
						updatedState.optionalMessage = '';
						updatedState.showMessageComposerUnderCheckbox = false;
					}

					return updatedState;
				});
				return;
			}

			addPrincipals({ principalsStagerPrincipals, queriesState, setMasterState });
		},
		commitStagerPrincipalsToSpecificAccess() {
			const { addMode } = masterState;
			addStagerPrincipalsToSpecificAccess({
				principalsStagerPrincipals: masterState.principalsStagerPrincipals,
				queriesState,
				addMode,
				setMasterState,
			});
		},
		changeAddMode(
			addMode: StagedPrincipalsAddMode,
			principalsStagerPrincipals: UserPickerPrincipal[],
		) {
			// In persistence experiment staged mode, changing add mode should not
			// commit principals into Specific Access until the user clicks Add.
			const { persistenceMode } = derivedState;
			if (
				persistenceMode === 'staged' &&
				expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
			) {
				// In staged mode (publish modal), just update the addMode state.
				// Principals will be committed when the user clicks the Add button.
				setMasterState(
					(s: MasterState): MasterState => ({
						...s,
						addMode,
						principalsStagerPrincipals,
					}),
				);
				return;
			}

			// In live mode or when experiment is off, re-stage principals with the new role
			addPrincipals({ principalsStagerPrincipals, addMode, queriesState, setMasterState });
		},
		changeSpecificAccessRole(role: Role, id: string) {
			stagePrincipalReassignment(setMasterState, role, id);
		},
		removeSpecificAccessPrincipal(id: string) {
			stagePrincipalRemoval(setMasterState, id);
		},
		removeAllSpecificAccessPrincipals() {
			setMasterState((s): MasterState => {
				const nextUpdatedState: MasterState = {
					...s,
					specificAccessDelta: {
						...s.specificAccessDelta,
						removeAll: true,
						removals: new Set(),
						reassignments: new Map(),
					},
					isSpecificAccessExpanded: false,
				};

				return nextUpdatedState;
			});
		},

		dismissSpaceAccessDetails() {
			setMasterState((s: MasterState) => ({
				...s,
				isSpaceAccessDetailsOpen: false,
			}));
		},
		openSpaceAccessDetails() {
			setMasterState((s: MasterState) => ({
				...s,
				isSpaceAccessDetailsOpen: true,
			}));
		},
		showOpenAccessWarningDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isOpenAccessWarningDialogOpen: true,
				shouldGeneralAccessSelectMenuBeControlled: true,
			}));
		},
		closeOpenAccessWarningDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isOpenAccessWarningDialogOpen: false,
				shouldGeneralAccessSelectMenuBeControlled: false,
			}));
		},
		cancelRestrictAccessConfirmation() {
			setMasterState((s: MasterState) => ({
				...s,
				isRestrictAccessConfirmationDialogOpen: false,
				generalAccessDelta: s.previousGeneralAccess ?? 'none',
				previousGeneralAccess: null,
				pendingGeneralAccess: null,
			}));
		},
		confirmRestrictAccessConfirmation() {
			setMasterState((s: MasterState) => ({
				...s,
				isRestrictAccessConfirmationDialogOpen: false,
				previousGeneralAccess: null,
				pendingGeneralAccess: null,
			}));

			if (expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
				const { persistenceMode } = derivedState;
				if (persistenceMode !== 'staged') {
					dispatchGeneralAccessMutation('restricted');
				}
			}
		},
		openInspectPermsDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isInspectPermsDialogOpen: true,
			}));
		},
		dismissInspectPermsDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isInspectPermsDialogOpen: false,
			}));
		},
		openPublicLinkDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isPublicLinkDialogOpen: true,
			}));
		},
		closePublicLinkDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isPublicLinkDialogOpen: false,
				lastTriggeredDialogVariant: 'publicLink',
			}));
		},
		openShareToSlackDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isShareToSlackDialogOpen: true,
			}));
		},
		closeShareToSlackDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isShareToSlackDialogOpen: false,
				lastTriggeredDialogVariant: 'shareToSlack',
			}));
		},
		openAccessRequestsDialog() {
			if (expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
				setMasterState((s: MasterState) => ({
					...s,
					isSpecificAccessDialogOpen: true,
					specificAccessDialogInitialTab: 1, // Requests tab
				}));
			} else {
				setMasterState((s: MasterState) => ({
					...s,
					isAccessRequestsDialogOpen: true,
				}));
			}
		},
		closeAccessRequestsDialog() {
			const { hasAccessRequestsChanges } = masterState;

			// Check if there are unsaved changes in the access requests dialog
			if (hasAccessRequestsChanges) {
				setMasterState(
					(s: MasterState): MasterState => ({
						...s,
						isCloseConfirmationOpen: true,
					}),
				);
			} else {
				setMasterState((s: MasterState) => ({
					...s,
					isAccessRequestsDialogOpen: false,
					lastTriggeredDialogVariant: 'accessRequests',
				}));

				void refetchQueries();
			}
		},
		clearElementToFocus() {
			setMasterState((s: MasterState) => ({
				...s,
				lastTriggeredDialogVariant: null,
			}));
		},
		showFlag(flag) {
			return controllerExtras.showFlag(flag);
		},
		showErrorFlag(flag) {
			return controllerExtras.showErrorFlag(flag);
		},
		handleCtaClick() {
			const { contentId, contentType, contentSubType, isUnpublishedDraft } = controllerProps;
			const { numberOfAncestors, userCanEditRestrictions, currentUser, edition } = queriesState;
			const { principalsStagerPrincipals, optionalMessage, shouldResolveMismatchedRestrictions } =
				masterState;
			const {
				isChanged,
				generalAccess,
				natureOfCta,
				stagedNotificationRecipients,
				isPublishModalFlow,
				includeInvites,
				persistenceMode,
			} = derivedState;
			const { addMode } = masterState;
			const {
				mutateContentRestrictions,
				exportPublishModalRestrictions,
				sendNotification,
				successFlag,
				errorFlag,
				fireAnalyticEvent,
				teardownController: { tearDownAfterPotentialFinalDialogCompletesClose },
				experienceTracker,
				callResolveMismatchedRestrictions,
				mutateRestrictionsAccess,
			} = controllerExtras;

			const beginTeardown = () => {
				// Tell the teardown manager to unmount the controller after the dialog
				// closes
				tearDownAfterPotentialFinalDialogCompletesClose();

				// Switch the active dialog to an empty one, to close it. Its
				// `onCloseComplete` will trigger the unmounting
				setMasterState((s2: MasterState) => ({
					...s2,
					sessionState: 'exiting',
					hasAccessRequestsChanges: false,
					// Deliberately don't set `isSaving: false` here. If we did,
					// the elements would regain their interactivity, meaning:
					// 1) the dialog will pop during its exit animation
					// 2) what if somebody then clicked one of those controls?
				}));
			};

			// We need to ensure we retrieve the most updated principal map to use during a CTA click.
			const updatedPrincipalMap = makePrincipalsMap(masterState, queriesState, {
				shouldIncludeUserPickerStagedAdditions: true,
				generalAccess,
				persistenceMode,
			});

			if (isPublishModalFlow) {
				exportPublishModalRestrictions(generalAccess, updatedPrincipalMap);
				beginTeardown();
			} else {
				dispatchCtaFlowEvent({
					contentId,
					edition,
					numberOfAncestors,
					type: 'cta',
					addMode,
					mutateContentRestrictions,
					currentUser,
					sendNotification,
					successFlag,
					errorFlag,
					beginTeardown,
					isMutatingAction:
						natureOfCta === 'notify' &&
						expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
							? false
							: isChanged,
					isNotifyingAction:
						natureOfCta === 'share' ||
						(natureOfCta === 'notify' &&
							expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')),
					userCanEditRestrictions,
					generalAccess: generalAccess,
					principalsMap: updatedPrincipalMap,
					optionalMessage,
					principalsStagerPrincipals,
					stagedNotificationRecipients,
					validateRestrictedPrincipals,
					experienceTracker,
					fireAnalyticEvent,
					contentSubType,
					contentType,
					callResolveMismatchedRestrictions,
					isResolvingAction: shouldResolveMismatchedRestrictions,
					isUnpublishedDraft,
					includeInvites,
					refreshDialogState,
					//general access updates do not happen on the cta click anymore in live mode
					mutateGeneralAccess: undefined,
					isGeneralAccessMutation: false,
					mutateRestrictionsAccess,
					// when clicking the cta button and the experiment is on this should be always true
					isRestrictionsAccessMutation: expValEquals(
						'persistence_specific_access_gate',
						'cohort',
						'experiment',
					),
				});
			}
		},
		changeOptionalMessage(message: string) {
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					optionalMessage: message,
				}),
			);
		},
		setNotifyChecked(newValue: boolean) {
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					notifyCheckedIfAvailable: newValue,
					// If we're unchecking the box, then if we check it again we want the
					// 'Add message' textarea to not show by default
					showMessageComposerUnderCheckbox: newValue ? s.showMessageComposerUnderCheckbox : false,
				}),
			);
		},
		toggleNotifyAddMessage() {
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					showMessageComposerUnderCheckbox: !s.showMessageComposerUnderCheckbox,
				}),
			);
		},
		doNotifyGroupsConfirmed() {
			dispatchCtaFlowEvent({ type: 'doNotifyGroups' });
		},
		doNotNotifyGroupsConfirmed() {
			dispatchCtaFlowEvent({ type: 'doNotNotifyGroups' });
		},
		doNotifyManyUsersConfirmed() {
			dispatchCtaFlowEvent({ type: 'doNotifyManyUsers' });
		},
		cancelNotifyManyUsers() {
			dispatchCtaFlowEvent({ type: 'cancelNotifyManyUsers' });
		},
		// Defaulting isGuestError to false as this is an edge case that is being handled.
		// A better solution for handling guest errors will be implemented in the future.
		// https://product-fabric.atlassian.net/browse/USD-633
		ignoreRestrictionsAndContinue(isGuestError = false) {
			dispatchCtaFlowEvent({ type: 'ignoreRestrictions', shouldSkipFlagRaise: isGuestError });
		},
		resolveRestrictionsComplete() {
			dispatchCtaFlowEvent({ type: 'resolveRestrictionsComplete' });
		},
		toggleSpecificAccessExpanded() {
			setMasterState((s: MasterState) => ({
				...s,
				isSpecificAccessExpanded: !s.isSpecificAccessExpanded,
			}));
		},
		openSpecificAccessDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isSpecificAccessDialogOpen: true,
				specificAccessDialogInitialTab: 0,
			}));
		},
		closeSpecificAccessDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isSpecificAccessDialogOpen: false,
				specificAccessDialogInitialTab: 0,
			}));
		},
		closeAccessRequestsResolveRestrictionsDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isAccessRequestsResolveRestrictionsDialogOpen: false,
				accessRequestsCurrentUserAddMode: undefined,
			}));
		},
		closeAccessRequestsWarnRestrictionsDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isAccessRequestsWarnRestrictionsDialogOpen: false,
			}));
		},
		async maybeStartBlockedAccessFlowForUser({
			id,
			avatarUrl,
			name,
			email,
			sitePermissionType,
			accessType,
		}) {
			const { contentId } = controllerProps;
			const { numberOfAncestors, edition } = queriesState;

			const { fireAnalyticEvent, cloudId } = controllerExtras;
			const addMode: MasterState['accessRequestsCurrentUserAddMode'] =
				accessType === 'EDIT' ? 'as-editor' : 'as-viewer';
			const userPrincipal: UserPrincipal = {
				id,
				avatarUrl,
				name,
				email,
				sitePermissionType,
				knownErrors: undefined,
			};

			// validate if restricted access can be fixed and preload space role mode in parallel
			const [validationResult] = await Promise.all([
				validateRestrictedPrincipals({
					contentId,
					cloudId,
					edition,
					numberOfAncestors,
					addMode,
					stagedUserPrincipals: [userPrincipal],
					setMasterState,
					fireAnalyticEvent,
				}),
				preloadSpaceRoleMode(cloudId),
			]);

			const { showWarnRestrictionsDialog, showResolveRestrictionsDialog } = validationResult;

			if (showWarnRestrictionsDialog) {
				setMasterState((s: MasterState) => ({
					...s,
					isAccessRequestsWarnRestrictionsDialogOpen: true,
				}));
			}

			if (showResolveRestrictionsDialog) {
				setMasterState((s: MasterState) => ({
					...s,
					isAccessRequestsResolveRestrictionsDialogOpen: true,
					accessRequestsCurrentUserAddMode: addMode,
				}));
			}

			return {
				isBlockedAccessFlowStarted: showWarnRestrictionsDialog || showResolveRestrictionsDialog,
			};
		},
		setAccessRequestsChanges(hasChanges: boolean) {
			setMasterState((s: MasterState) => ({
				...s,
				hasAccessRequestsChanges: hasChanges,
			}));
		},
		markPrincipalsAsSkipped(skippedPrincipals: string[]) {
			const { principalsStagerPrincipals } = masterState;
			setMasterState((s: MasterState) => ({
				...s,
				principalsStagerPrincipals: principalsStagerPrincipals.map((principal) => {
					if (isEmail(principal) && skippedPrincipals.includes(principal.id)) {
						return { ...principal, isPendingAction: false };
					}
					return principal;
				}),
			}));
		},
		markPrincipalsAsInvited(
			invitedPrincipals: { id: string; email: string }[],
			errorPrincipals: { email: string }[],
		) {
			const { principalsStagerPrincipals } = masterState;
			const principalsToAdd: ({ type: 'user' } & UserPrincipal)[] = invitedPrincipals.map(
				({ email, id }) => {
					const principal = principalsStagerPrincipals.find((p) => email === p.id);
					return {
						type: 'user',
						id,
						email,
						name: principal?.name || email,
						avatarUrl: principal?.avatarUrl || '',
						knownErrors: { accessWarning: 'pending-site-access-warning', parentLink: null },
						sitePermissionType: 'none',
					};
				},
			);
			principalsToAdd.forEach((principal) => {
				stagePrincipalAddition(setMasterState, principal);
			});
			setMasterState((s: MasterState) => ({
				...s,
				principalsStagerPrincipals: s.principalsStagerPrincipals.map((principal) => {
					const invitedPrincipal = principalsToAdd.find(({ email }) => email === principal.id);
					if (invitedPrincipal && (isEmail(principal) || isExternalUser(principal))) {
						return invitedPrincipal;
					}
					const errorPrincipal = errorPrincipals.find(({ email }) => email === principal.id);
					if (isEmail(principal) && errorPrincipal) {
						return { ...principal, isPendingAction: false };
					}
					return principal;
				}),
			}));
		},
	};
}
